'use client';

/**
 * LE LIVRE — THE BOOK. Vue dédiée (aussi intégrée dans la page fusionnée READ) :
 * bannière royale, accès (lire/écouter/mushaf),
 * et la galerie des 29 éditions prestige du Saint Coran (photos réelles).
 */
import { useState } from 'react';
import Image from 'next/image';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import products from '@/data/products.json';
import { cn } from '@/lib/utils';
import {
  BookOpen, Headphones, ScrollText, ChevronRight, X, Crown,
} from 'lucide-react';

const CORAN_EDITIONS = (products as { id: string; cat: string; brand: string; label: string }[])
  .filter((p) => p.cat === 'coran');

interface Props {
  /** Dans la page fusionnée READ : scrolle vers l'ancre au lieu de changer de vue */
  onNavigate?: (anchor: 'read' | 'listen' | 'sourates') => void;
}

export default function LivreSection({ onNavigate }: Props = {}) {
  const { uiLang, setSection } = useApp();
  const t = DICTS[uiLang];
  const [zoom, setZoom] = useState<{ src: string; label: string; brand: string } | null>(null);

  const go = (key: 'read' | 'listen' | 'sourates') => {
    if (onNavigate) onNavigate(key);
    else setSection(key);
  };

  const access = [
    { key: 'read', label: t.book.access_read, desc: t.book.access_read_desc, icon: <BookOpen className="h-6 w-6" /> },
    { key: 'listen', label: t.book.access_listen, desc: t.book.access_listen_desc, icon: <Headphones className="h-6 w-6" /> },
    { key: 'sourates', label: t.book.access_mushaf, desc: t.book.access_mushaf_desc, icon: <ScrollText className="h-6 w-6" /> },
  ] as const;

  return (
    <section aria-label={t.book.title}>
      {/* ===== Bannière royale ===== */}
      <div className="relative overflow-hidden border-b border-[rgba(212,175,55,0.3)]">
        <Image
          src="/images/banners/livre_v2.png"
          alt="The Holy Quran — coffret royal"
          width={1600} height={640} priority unoptimized
          className="h-[300px] w-full object-cover sm:h-[380px]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,25,19,0.35),rgba(3,32,25,0.88))]" aria-hidden />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.4em] text-[#f0d98c]/90">{t.book.eyebrow}</p>
          <h1 className="font-serif-royal text-5xl font-bold sm:text-6xl">
            <span className="text-gold-gradient">{t.book.title}</span>
          </h1>
          <p className="font-arabic mt-1 text-3xl text-[#f0d98c]" dir="rtl" lang="ar">{t.book.title_ar}</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {/* ===== Sous-titre ===== */}
        <p className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-[#cfe0cf]/85 sm:text-base">
          {t.book.subtitle}
        </p>

        {/* ===== Accès au Livre ===== */}
        <div className="mt-10">
          <div className="ornament-divider mb-6">
            <h2 className="font-serif-royal text-2xl font-bold text-[#f0d98c]">{t.book.access_title}</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {access.map((a) => (
              <button
                key={a.key}
                onClick={() => go(a.key)}
                className="glass-gold glass-gold-hover group rounded-2xl p-6 text-left"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.1)] text-[#d4af37] transition-transform duration-300 group-hover:scale-110">
                  {a.icon}
                </span>
                <h3 className="mt-4 text-lg font-bold text-[#f5f0e1] group-hover:text-[#f0d98c]">{a.label}</h3>
                <p className="mt-1 text-[12.5px] leading-relaxed text-[#cfe0cf]/65">{a.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-[#d4af37]">
                  {t.book.access_cta}
                  <ChevronRight className="h-3.5 w-3.5 rtl-flip" aria-hidden />
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== Éditions ===== */}
        <div className="mt-12">
          <div className="ornament-divider mb-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)]">
              <Crown className="h-4 w-4 text-[#d4af37]" aria-hidden />
            </span>
          </div>
          <div className="mb-7 text-center">
            <h2 className="font-serif-royal text-2xl font-bold sm:text-3xl">
              <span className="text-gold-gradient">{t.book.editions_title}</span>
            </h2>
            <p className="mt-1.5 text-sm text-[#cfe0cf]/70">{t.book.editions_sub}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {CORAN_EDITIONS.map((p) => (
              <button
                key={p.id}
                onClick={() => setZoom({ src: `/images/products/${p.id}`, label: p.label, brand: p.brand })}
                className="group relative aspect-square overflow-hidden rounded-xl border border-[rgba(212,175,55,0.25)] transition-all hover:border-[rgba(212,175,55,0.75)] hover:shadow-[0_0_26px_rgba(212,175,55,0.3)]"
                aria-label={p.label}
              >
                <Image
                  src={`/images/products/${p.id}`} alt={p.label} fill unoptimized
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 16vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[rgba(2,25,19,0.92)] to-transparent px-2 pb-1.5 pt-6 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="block truncate text-[10px] font-bold text-[#f0d98c]">{p.label}</span>
                  <span className="block truncate text-[9px] uppercase tracking-wider text-[#cfe0cf]/60">{p.brand}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="mt-7 text-center">
            <button
              onClick={() => setSection('univers')}
              className="gold-glow-hover rounded-xl border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)] px-7 py-3 text-sm font-bold text-[#f0d98c] transition-colors hover:bg-[rgba(212,175,55,0.2)]"
            >
              {t.book.editions_cta}
            </button>
          </div>
        </div>
      </div>

      {/* ===== Lightbox ===== */}
      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(1,15,11,0.94)] p-4 backdrop-blur-sm"
          onClick={() => setZoom(null)}
          role="dialog"
          aria-modal="true"
          aria-label={zoom.label}
        >
          <div className="relative max-h-[88vh] w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <Image
              src={zoom.src} alt={zoom.label} width={1024} height={1024} unoptimized
              className={cn('mx-auto max-h-[78vh] w-auto rounded-xl border border-[rgba(212,175,55,0.5)] object-contain shadow-[0_0_60px_rgba(212,175,55,0.25)]')}
            />
            <div className="mt-3 text-center">
              <p className="font-serif-royal text-lg font-bold text-[#f0d98c]">{zoom.label}</p>
              <p className="text-xs uppercase tracking-widest text-[#cfe0cf]/60">{zoom.brand}</p>
            </div>
            <button
              onClick={() => setZoom(null)}
              className="absolute -top-2 right-0 flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(3,32,25,0.9)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.2)]"
              aria-label={t.common.close}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
